import { useSelector } from 'react-redux';
import type { Dispatch, RefObject, SetStateAction, VoidFunctionComponent } from 'react';

import * as PackageInventoryStore from '../../../../redux/packageInventory';
import FallbackImage from '../../../generic/FallbackImage';
import type { PackageVersionVulnerability } from '../../../../../shared/organizationPackages/response';
import type { SortableColumn, SortType } from '../types';

import VulnerabilitiesTableBody from './Body';
import VulnerabilitiesTableTableHeader from './Header';

import css from './VulnerabilitiesTable.module.less';

type VulnerabilitiesTableProps = {
  vulnerabilities: PackageVersionVulnerability[];
  sortByColumn: SortableColumn;
  setSortByColumn: Dispatch<SetStateAction<SortableColumn>>;
  sortType: SortType;
  setSortType: Dispatch<SetStateAction<SortType>>;
  onVulnSelect: (vulnId: string) => void;
  tableBodyRef: RefObject<HTMLTableSectionElement>;
};

const VulnerabilitiesTable: VoidFunctionComponent<VulnerabilitiesTableProps> = ({
  vulnerabilities,
  setSortByColumn,
  sortByColumn,
  sortType,
  setSortType,
  onVulnSelect,
  tableBodyRef,
}) => {
  const isVulnerabilitiesEmpty = useSelector(PackageInventoryStore.selectors.getIsVulnerabilitiesEmpty);
  const packageVulnerabilities = useSelector(PackageInventoryStore.selectors.getAsyncStatusGetPackageVulnerabilities);
  let tableContent = (
    <>
      <VulnerabilitiesTableTableHeader
        sortByColumn={sortByColumn}
        setSortByColumn={setSortByColumn}
        sortType={sortType}
        setSortType={setSortType}
      />
      <VulnerabilitiesTableBody
        vulnerabilities={vulnerabilities}
        onVulnSelect={onVulnSelect}
        tableBodyRef={tableBodyRef}
      />
    </>
  );

  if (isVulnerabilitiesEmpty) {
    tableContent = (
      <FallbackImage src="tada" alt="No vulnerabilities found" hasPadding>
        No vulnerabilities found.
      </FallbackImage>
    );
  }

  if (packageVulnerabilities.errors?.length) {
    tableContent = (
      <FallbackImage src="error-plug" alt="Network error" hasPadding>
        <div>Oops! There was an error.</div>
        <div>Please try again in a bit.</div>
      </FallbackImage>
    );
  }

  return <table className={css.table}>{tableContent}</table>;
};

export default VulnerabilitiesTable;
